import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import { NotificationAlert } from "src/components/common";
import {
    SelectFormInput,
    TextFormInput,
} from "src/components/form";
import { renderRegistrationFormSaveBlock } from "src/helpers/alert";
import {
    InstitutionCodeBreadcrumbs,
    InstitutionCreateCodeBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import {
    RegistrationComponent,
    renderFormControls,
} from "src/helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "src/helpers/lookupHelper";
import useNavigate from "src/hooks/useNavigate";
import { RegistrationFormComponent } from "src/interfaces/forms/IRegistrationComponent";
import { IRegistrationFormState } from "src/reducers/reactReduxForms";
import { IInstitutionCodeReduxFormState } from "src/reducers/reactReduxForms/institutionCode";
import * as InstitutionActions from "../../../actions/institution";
import * as InstitutionCodeActions from "../../../actions/institutionCode";
import * as NavigationActions from "../../../actions/navigation";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { InstitutionCodeSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./InstitutionCodePage.scss";

interface InstitutionCodePageProps {
    form: Dtos.InstitutionCode & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: IInstitutionCodeReduxFormState;
    institutionCode: Dtos.InstitutionCode;
    institution: Dtos.Institution;
    lookups: Dtos.Lookup[];
    loadingInstitutionCode: boolean;
    loadInstitutionCodeSuccess: boolean;
    loadInstitutionCodeFailure: boolean;
    loadInstitutionCodeErrorCode: string;
    savingInstitutionCode: boolean;
    saveInstitutionCodeSuccess: boolean;
    saveInstitutionCodeFailure: boolean;
    creatingInstitutionCode: boolean;
    createInstitutionCodeSuccess: boolean;
    createInstitutionCodeFailure: boolean;
    validatingInstitutionCode: boolean;
    loadingInstitution: boolean;
    loadInstitutionSuccess: boolean;
    loadInstitutionFailure: boolean;
    loadInstitutionErrorCode: string;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearInstitutionCode: InstitutionCodeActions.IInstitutionCodeClearActionCreator;
    loadInstitutionCode: InstitutionCodeActions.IInstitutionCodeLoadByIdActionCreator;
    saveInstitutionCode: InstitutionCodeActions.IInstitutionCodeSaveActionCreator;
    createInstitutionCode: InstitutionCodeActions.IInstitutionCodeCreateActionCreator;
    validateInstitutionCode: InstitutionCodeActions.IInstitutionCodeValidateActionCreator;
    clearInstitution: InstitutionActions.IInstitutionClearActionCreator;
    loadInstitution: InstitutionActions.IInstitutionLoadByIdActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.institutionCode";

const InstitutionCodePage: FC<InstitutionCodePageProps> = ({
    form,
    formState,
    formProperties,
    institutionCode,
    institution,
    lookups,
    loadingInstitutionCode,
    loadInstitutionCodeFailure,
    loadInstitutionCodeErrorCode,
    savingInstitutionCode,
    saveInstitutionCodeSuccess,
    saveInstitutionCodeFailure,
    creatingInstitutionCode,
    validatingInstitutionCode,
    loadingInstitution,
    loadInstitutionFailure,
    loadInstitutionErrorCode,
    validationFailures,
    permissions,
    clearInstitutionCode,
    loadInstitutionCode,
    saveInstitutionCode,
    createInstitutionCode,
    validateInstitutionCode,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    loadInstitution,
    clearInstitution
}) => {
    //
    // -- Params --
    const { institutionId } = useParams<{ institutionId: string }>();
    const { institutionCodeId } = useParams<{ institutionCodeId: string }>();
    const institutionIdNumber = parseInt(institutionId, 10);
    const institutionCodeIdNumber = parseInt(institutionCodeId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);
    const [isNew, setIsNew] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load Institution Code
    useEffect(() => {
        if (institutionIdNumber && institutionCodeIdNumber && !institutionCode) {
            setDrawerItems(
                InstitutionCodeSubMenu(
                    institutionIdNumber,
                    institutionCodeIdNumber,
                    permissions
                )
            );
            setBreadcrumbs(
                InstitutionCodeBreadcrumbs(
                    institutionIdNumber,
                    institutionCodeIdNumber
                )
            );

            loadInstitutionCode(institutionCodeIdNumber);
            loadInstitution(institutionIdNumber);
        } else if (
            institutionIdNumber &&
            !institutionCodeIdNumber &&
            !institutionCode
        ) {
            setBreadcrumbs(
                InstitutionCreateCodeBreadcrumbs(institutionIdNumber)
            );
            setDrawerItems([]);
            createInstitutionCode(institutionIdNumber);
            loadInstitution(institutionIdNumber);
            setIsNew(true);
        } else if (institutionCode && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        institutionIdNumber,
        institutionCodeIdNumber,
        institutionCode,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        InstitutionCodeSubMenu,
        InstitutionCodeBreadcrumbs,
        InstitutionCreateCodeBreadcrumbs,
        createInstitutionCode,
        setFormInitalised,
        setIsNew
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionFailure) {
            navigate(`/error/${loadInstitutionErrorCode}`);
        }
        if (loadInstitutionCodeFailure) {
            navigate(`/error/${loadInstitutionCodeErrorCode}`);
        }
    }, [
        loadInstitutionFailure,
        loadInstitutionCodeFailure,
        loadInstitutionErrorCode,
        loadInstitutionCodeErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            institutionIdNumber &&
            institutionCodeIdNumber &&
            institution &&
            institutionCode
        ) {
            setBreadcrumbs(
                InstitutionCodeBreadcrumbs(
                    institutionIdNumber,
                    institutionCodeIdNumber,
                    institution?.name,
                    institutionCode?.codeValue
                )
            );
        } else if (
            institutionIdNumber &&
            !institutionCodeIdNumber &&
            institution &&
            !institutionCode
        ) {
            setBreadcrumbs(
                InstitutionCreateCodeBreadcrumbs(
                    institutionIdNumber,
                    institution?.name
                )
            );
        }
    }, [
        institutionIdNumber,
        institutionCodeIdNumber,
        institution,
        institutionCode,
        setBreadcrumbs,
        InstitutionCodeBreadcrumbs,
        InstitutionCreateCodeBreadcrumbs,
    ]);

    // Reload institution code after saving
    useEffect(() => {
        if (!savingInstitutionCode && saveInstitutionCodeSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Institution code saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(`/institutions/${institutionIdNumber}/codes`);
            } else if (!institutionCodeIdNumber) {
                navigate(
                    `/institutions/${institutionIdNumber}/codes/${institutionCode?.id}/details`
                );
                clearInstitutionCode();
                clearInstitution();
            }
        }
    }, [
        saveAndReturn,
        savingInstitutionCode,
        saveInstitutionCodeSuccess,
        institutionIdNumber,
        institutionCodeIdNumber,
        institutionCode,
        clearInstitutionCode,
        clearInstitution,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingInstitutionCode && saveInstitutionCodeFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingInstitutionCode, saveInstitutionCodeFailure]);

    // Cleanup effect to clear the personnel on component unmount
    useEffect(() => {
        return () => {
            clearInstitutionCode();
            clearInstitution();
            resetForm(reduxFormName);
        };
    }, [clearInstitutionCode, clearInstitution, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.InstitutionCode = {} as Dtos.InstitutionCode;

            if (!data.institutionId) {
                changes.institutionId = institutionIdNumber;
            }

            await saveInstitutionCode(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.InstitutionCode) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        navigate(`/institutions/${institutionIdNumber}/codes`);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.InstitutionCode = {} as Dtos.InstitutionCode;

        loadForm(
            reduxFormName,
            Object.assign({ ...institutionCode }, extraData)
        );
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.InstitutionCode = {} as Dtos.InstitutionCode;

            if (form && hasBeenSubmitted && !validatingInstitutionCode) {
                await validateInstitutionCode(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                undefined,
                institutionCodeIdNumber,
                permissions,
                Dtos.Permission.InstitutionCodeUpdate,
                Dtos.Permission.InstitutionCodeCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingInstitutionCode ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled) ||
            !isNew;

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            codeDetails: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row py-4 px-4">
                                <div className="col-sm-12 col-lg-6">
                                    {renderFormControls(
                                        form,
                                        "institutionCode",
                                        {
                                            institutionCodingId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "InstitutionCode",
                                                                "institutionCodingId",
                                                                lookups
                                                            ),
                                                            form.institutionCodingId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-lg-5",
                                                fieldClassName:
                                                    "col-sm-12 col-lg-6 generic-form-field mr-0 pr-0",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-sm-12 col-lg-6">
                                    {renderFormControls(
                                        form,
                                        "institutionCode",
                                        {
                                            codeValue: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-lg-3",
                                                fieldClassName:
                                                    "col-sm-12 col-lg-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            }
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "institutionCode",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        institutionCodeIdNumber,
        permissions,
        savingInstitutionCode,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.InstitutionCodeView}
            loading={
                loadingInstitutionCode ||
                savingInstitutionCode ||
                creatingInstitutionCode ||
                loadingInstitution
            }
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<InstitutionCodePageProps> => {
    let institutionCode: Dtos.InstitutionCode | undefined = !(
        state.institutionCodes.formData instanceof Array
    )
        ? state.institutionCodes.formData
        : undefined;
    let institution: Dtos.Institution | undefined = !(
        state.institutions.formData instanceof Array
    )
        ? state.institutions.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        institutionCode: institutionCode!,
        institution: institution!,
        loadingInstitutionCode:
            state.institutionCodes.loadState &&
            state.institutionCodes.loadState.status == RequestState.Pending,
        loadInstitutionCodeSuccess:
            state.institutionCodes.loadState &&
            state.institutionCodes.loadState.status == RequestState.Success,
        loadInstitutionCodeFailure:
            state.institutionCodes.loadState &&
            state.institutionCodes.loadState.status == RequestState.Failure,
        loadInstitutionCodeErrorCode: state.institutionCodes.loadState.errorCode,
        savingInstitutionCode:
            state.institutionCodes.saveState &&
            state.institutionCodes.saveState.status == RequestState.Pending,
        saveInstitutionCodeSuccess:
            state.institutionCodes.saveState &&
            state.institutionCodes.saveState.status == RequestState.Success,
        saveInstitutionCodeFailure:
            state.institutionCodes.saveState &&
            state.institutionCodes.saveState.status == RequestState.Failure,
        creatingInstitutionCode:
            state.institutionCodes.createState &&
            state.institutionCodes.createState.status == RequestState.Pending,
        createInstitutionCodeSuccess:
            state.institutionCodes.createState &&
            state.institutionCodes.createState.status == RequestState.Success,
        createInstitutionCodeFailure:
            state.institutionCodes.createState &&
            state.institutionCodes.createState.status == RequestState.Failure,
        validatingInstitutionCode:
            state.institutionCodes.validateState &&
            state.institutionCodes.validateState.status == RequestState.Pending,
        loadingInstitution:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Pending,
        loadInstitutionSuccess:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Success,
        loadInstitutionFailure:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Failure,
        loadInstitutionErrorCode: state.institutions.loadState.errorCode,
        user: state.user.data,
        permissions: permissions,
        lookups: state.institutionCodes.lookups,
        form: state.reduxForms.institutionCode,
        formState: state.institutionCodes.formState,
        formProperties: state.institutionCodes.formProperties,
        reduxFormState: state.reduxForms.formState.institutionCode,
        validationFailures: institutionCode
            ? state.institutionCodes.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<InstitutionCodePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadInstitutionCode: bindActionCreators(InstitutionCodeActions.LoadInstitutionCodeById, dispatch),
        clearInstitutionCode: bindActionCreators(InstitutionCodeActions.Clear, dispatch),
        saveInstitutionCode: bindActionCreators(InstitutionCodeActions.SaveInstitutionCode, dispatch),
        createInstitutionCode: bindActionCreators(InstitutionCodeActions.CreateInstitutionCode, dispatch),
        validateInstitutionCode: bindActionCreators(InstitutionCodeActions.ValidateInstitutionCode, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        loadInstitution: bindActionCreators(InstitutionActions.LoadInstitutionById, dispatch),
        clearInstitution: bindActionCreators(InstitutionActions.Clear, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(InstitutionCodePage);
